/**
 * Slack OAuth and account-link callback parameters are captured before any
 * telemetry client or router initializes. Credentials and outcome codes move
 * to sessionStorage only after the address bar has been synchronously scrubbed.
 */
import {
  POST_AUTH_REDIRECT_KEY,
  SLACK_REDIRECT_MARKER_KEY,
} from '@/lib/post-auth-redirect';

export type SlackOAuthOutcome = {
  error: string | null;
  connected: string | null;
};

type StoredSlackCallback = {
  error?: string;
  connected?: string;
  linkToken?: string;
};

const SLACK_CALLBACK_STORAGE_KEY = 'cleanlist:slack-callback';

let inMemoryCallback: StoredSlackCallback = {};

function parseStoredCallback(raw: string | null): StoredSlackCallback {
  if (!raw) return {};
  try {
    const value = JSON.parse(raw) as Record<string, unknown>;
    return {
      error: typeof value.error === 'string' ? value.error : undefined,
      connected:
        typeof value.connected === 'string' ? value.connected : undefined,
      linkToken:
        typeof value.linkToken === 'string' ? value.linkToken : undefined,
    };
  } catch {
    return {};
  }
}

function readStoredCallback(): StoredSlackCallback {
  try {
    const raw = window.sessionStorage.getItem(SLACK_CALLBACK_STORAGE_KEY);
    return raw ? parseStoredCallback(raw) : inMemoryCallback;
  } catch {
    return inMemoryCallback;
  }
}

function writeStoredCallback(value: StoredSlackCallback): void {
  const compact = Object.fromEntries(
    Object.entries(value).filter(([, entry]) => entry !== undefined)
  ) as StoredSlackCallback;
  inMemoryCallback = compact;
  try {
    if (Object.keys(compact).length === 0) {
      window.sessionStorage.removeItem(SLACK_CALLBACK_STORAGE_KEY);
    } else {
      window.sessionStorage.setItem(
        SLACK_CALLBACK_STORAGE_KEY,
        JSON.stringify(compact)
      );
    }
  } catch {
    // The same-page signed-in flow can continue from the in-memory fallback.
  }
}

function captureSlackCallback(): void {
  if (typeof window === 'undefined') return;

  const url = new URL(window.location.href);
  const hashParams = new URLSearchParams(url.hash.replace(/^#\??/, ''));
  const hasSlackHashCallback = [
    'slack_error',
    'slack_connected',
    'slack_link',
  ].some((key) => hashParams.has(key));
  const hasSlackQueryCallback = [
    'slack_error',
    'slack_connected',
    'slack_link',
  ].some((key) => url.searchParams.has(key));
  const error =
    url.searchParams.get('slack_error') ?? hashParams.get('slack_error');
  const connected =
    url.searchParams.get('slack_connected') ??
    hashParams.get('slack_connected');
  // Account-link credentials are accepted only from the fragment. A query
  // token has already crossed the CDN / edge request line, so scrub it but do
  // not trust or persist it. The query form is intentionally not supported as
  // a compatibility path.
  const linkToken = hashParams.get('slack_link');

  if (!hasSlackQueryCallback && !hasSlackHashCallback) return;

  url.searchParams.delete('slack_error');
  url.searchParams.delete('slack_connected');
  url.searchParams.delete('slack_link');
  if (hasSlackHashCallback) {
    hashParams.delete('slack_error');
    hashParams.delete('slack_connected');
    hashParams.delete('slack_link');
    url.hash = hashParams.toString() ? `#${hashParams.toString()}` : '';
  }

  const cleanUrl = `${url.pathname}${url.search}${url.hash}`;
  window.history.replaceState(window.history.state, '', cleanUrl);

  // Do not touch storage until the credential-bearing location is gone.
  const previous = readStoredCallback();
  writeStoredCallback({
    ...previous,
    ...(error ? { error } : {}),
    ...(connected ? { connected } : {}),
    ...(linkToken ? { linkToken } : {}),
  });
  if (linkToken) {
    try {
      if (!window.sessionStorage.getItem(POST_AUTH_REDIRECT_KEY)) {
        window.sessionStorage.setItem(POST_AUTH_REDIRECT_KEY, '/integrations');
        window.sessionStorage.setItem(SLACK_REDIRECT_MARKER_KEY, '1');
      }
    } catch {
      // AuthRouter still routes the current page to authentication.
    }
  }
}

captureSlackCallback();

export function consumeSlackOAuthOutcome(): SlackOAuthOutcome | null {
  const callback = readStoredCallback();
  if (!callback.error && !callback.connected) return null;

  writeStoredCallback({ linkToken: callback.linkToken });
  return {
    error: callback.error ?? null,
    connected: callback.connected ?? null,
  };
}

export function getPendingSlackLinkToken(): string | null {
  return readStoredCallback().linkToken ?? null;
}

export function clearPendingSlackLinkToken(expectedToken: string): void {
  const callback = readStoredCallback();
  if (callback.linkToken !== expectedToken) return;
  writeStoredCallback({
    error: callback.error,
    connected: callback.connected,
  });
  try {
    if (window.sessionStorage.getItem(SLACK_REDIRECT_MARKER_KEY)) {
      if (
        window.sessionStorage.getItem(POST_AUTH_REDIRECT_KEY) ===
        '/integrations'
      ) {
        window.sessionStorage.removeItem(POST_AUTH_REDIRECT_KEY);
      }
      window.sessionStorage.removeItem(SLACK_REDIRECT_MARKER_KEY);
    }
  } catch {
    // Auth redirect storage is best-effort and never contains the token.
  }
}
